import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { filter, take, tap, withLatestFrom } from 'rxjs/operators'
import { BaseSiteService, Country, isNotNullable, SiteContext, SiteContextActions, SiteContextConfig, } from '@spartacus/core'
import { MbSiteContextActions } from '../store/actions'
import { StoreContextConfig } from '../../store-context/store-context-config'
import { MbConstants } from '../../shared/mb-constants'
import { BaseSiteUtilsService } from '../../shared/services/base-site-utils.service'
import { MbBaseSiteCountry } from '../../models'
import { MbSiteContextSelectors, MbStateWithSiteContext } from '../store'

/**
 * Facade that provides easy access to country-language state, actions and selectors.
 */
@Injectable({
  providedIn: 'root'
})
export class CountryLanguageService implements SiteContext<Country> {

  constants = MbConstants

  constructor (protected baseSiteService: BaseSiteService,
               protected store: Store<MbStateWithSiteContext>,
               protected config: SiteContextConfig,
               protected storeContextConfig: StoreContextConfig,
               protected baseSiteUtilsService: BaseSiteUtilsService) {
  }

  /**
   * Represents all the country-languages supported by the current store.
   */
  getAll () {
    return this.store.pipe(
      select(MbSiteContextSelectors.getAllCountries),
      tap(countries => !countries && this.store.dispatch(new MbSiteContextActions.LoadCountries())),
      filter(isNotNullable)
    )
  }

  /**
   * Represents the isocode of the active country-language.
   */
  getActive () {
    return this.store.pipe(
      select(MbSiteContextSelectors.getActiveCountry),
      filter(isNotNullable),
    )
  }

  /**
   * Sets the active site, country, language and currency.
   */
  setActive (isocode: string,
             currencyIsocode?: string) {
    let newActiveSite = this.storeContextConfig?.mbcomstore?.sites?.filter(
      (countrySite) => countrySite.country === isocode.split('-', 1)[0]).pop()

    if (!newActiveSite) {
      newActiveSite = this.storeContextConfig?.mbcomstore?.sites?.filter(
        (countrySite) => countrySite.country === undefined).pop()
    }

    if (!newActiveSite || !newActiveSite.site) {
      return
    }

    this.baseSiteService.setActive(newActiveSite?.site)

    const siteCountry = newActiveSite?.country
    let newCountryLanguage: any
    if (!siteCountry) {
      newCountryLanguage = this.config?.context ?
        this.config?.context['countryLanguage']?.filter((countryLanguage) => countryLanguage?.includes(this.constants.FALLBACK_COUNTRY))?.pop() ||
        null : null
    } else {
      newCountryLanguage = this.config?.context ?
        this.config?.context['countryLanguage']?.filter((countryLanguage) => countryLanguage?.includes(siteCountry))?.pop() || null
        : null
    }

    this.store
      .pipe(select(MbSiteContextSelectors.getActiveCountry), take(1))
      .subscribe((activeCountry) => {
        if (activeCountry !== isocode) {
          this.store.dispatch(
            new MbSiteContextActions.SetActiveCountry(newCountryLanguage)
          )
          this.store.dispatch(
            new SiteContextActions.SetActiveLanguage(newCountryLanguage?.split('-', 2)?.[1])
          )
        }
      })

    if (currencyIsocode) {
      this.store
        .pipe(select(MbSiteContextSelectors.getActiveCurrency), take(1))
        .subscribe((activeCurrency) => {
          if (activeCurrency !== currencyIsocode) {
            this.store.dispatch(
              new SiteContextActions.SetActiveCurrency(currencyIsocode)
            )
          }
        })
    } else {
      this.store
        .pipe(select(MbSiteContextSelectors.getActiveCurrency), take(1),
          withLatestFrom(this.baseSiteUtilsService.getCurrencyOfCountry(siteCountry)))
        .subscribe(([activeCurrency, currencyOfCountry]) => {
          if (currencyOfCountry?.isocode && activeCurrency !== currencyOfCountry.isocode) {
            this.store.dispatch(
              new SiteContextActions.SetActiveCurrency(currencyOfCountry?.isocode)
            )
          }
        })
    }
  }

  /**
   * Returns true if the given country has a base site and false otherwise.
   */
  getBaseSiteForCountry (countryIso?: string) {
    return this.storeContextConfig?.mbcomstore?.sites?.find((site: MbBaseSiteCountry) => site?.country === countryIso)?.site
  }
}
